import { useConfigurationContext } from "@/components/crm/root/ConfigurationContext.tsx";
import { AUTH_VISUAL } from "./authVisualConfig";

type AuthVisualPanelProps = {
  /** Banner image path (override the AUTH_VISUAL default to pick a variation). */
  image?: string;
  /** Optional brand tagline shown bottom-left on large screens. */
  tagline?: string;
  /** Optional supporting line under the tagline. */
  subcopy?: string;
};

/**
 * Left-side visual panel shared by the auth screens (login / request-access).
 *
 * Renders only on `lg+` (hidden on small screens, mirroring the previous
 * shell). A full-bleed brand banner sits behind a legibility scrim so the
 * wordmark and tagline stay readable while the art reads on the right side.
 */
export const AuthVisualPanel = ({
  image = AUTH_VISUAL.image,
  tagline = AUTH_VISUAL.tagline,
  subcopy = AUTH_VISUAL.subcopy,
}: AuthVisualPanelProps) => {
  const { darkModeLogo, title } = useConfigurationContext();

  return (
    <div className="relative hidden h-full flex-col overflow-hidden bg-zinc-900 p-10 text-white dark:border-r lg:flex">
      <img
        src={image}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
      {/* Legibility scrims: keep the left/bottom copy readable, let the art breathe on the right */}
      <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/90 via-zinc-950/45 to-zinc-950/10" />
      <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-zinc-950/85 via-zinc-950/40 to-transparent" />

      <div className="relative z-20 flex items-center text-lg font-medium">
        {darkModeLogo ? (
          <img className="h-6 mr-2" src={darkModeLogo} alt={title} />
        ) : null}
        {title}
      </div>

      <div className="relative z-20 mt-auto max-w-sm pb-2">
        {tagline ? (
          <p className="text-2xl font-semibold leading-tight tracking-tight">
            {tagline}
          </p>
        ) : null}
        {subcopy ? (
          <p className="mt-3 text-sm leading-relaxed text-white/70">{subcopy}</p>
        ) : null}
      </div>
    </div>
  );
};
